import type { FC } from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import * as creditAppSearchSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSearchSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as creditAppSearchActionCreators from '@makemydeal/dr-dash-store/actions/creditAppSearchActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { SlideOut } from '@interstate/components/SlideOut';
import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useCreditAppSearchForm } from './hooks/useCreditAppSearchForm';
import { useSearchCreditApps } from './hooks/useSearchCreditApps';
import { useDealComparisonData } from './hooks/useDealComparisonData';
import { SearchView } from './views/SearchView';
import { SearchResultsView } from './views/SearchResultsView';
import { ReviewView } from './views/ReviewView';
import { SlideoutContainer, LoadingContainer } from './CreditAppSearch.styles';
import { CreditAppSearchView, type CreditAppSearchResult } from './types';
import type { FooterActionsType } from '@interstate/components/FooterActions';
import { dealLockHooks } from '../hooks';

/**
 * Main credit app search slideout component
 * Manages slideout visibility via Redux and form state via local component state
 * Supports future multi-view functionality (Search and Review views)
 */
export const CreditAppSearchSlideOut: FC = () => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const isSlideOutOpen = useSelector(creditAppSearchSelectors.getSlideOutOpen);
    const shopperInfo = useSelector(offerReduxSelectors.getShopperInfo);
    const lastName = shopperInfo.lastName || '';
    const orignalDealRefId = useSelector(offerReduxSelectors.getDealRefId);
    const originalDealRefIdFs = useSelector(offerReduxSelectors.getDealRefIdFS);
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();

    // Form state management via custom hook with prepopulated last name
    const { formData, errors, touched, handleFieldChange, handleFieldBlur, clearForm, isSearchButtonEnabled } =
        useCreditAppSearchForm({
            lastName
        });

    // Search functionality via custom hook
    const { searchResults, isLoading, error, performSearch, clearResults, cancelSearch } = useSearchCreditApps();

    // Local component state for view management
    const [currentView, setCurrentView] = useState<CreditAppSearchView>(CreditAppSearchView.SEARCH);
    const [selectedCreditApp, setSelectedCreditApp] = useState<CreditAppSearchResult | null>(null);

    // Track if last name has been initialized for current slideout session
    const hasInitializedLastName = useRef<boolean>(false);
    // Track if initial search has been performed for current slideout session
    const hasPerformedInitialSearch = useRef<boolean>(false);

    // Get deal comparison data for review view
    const { currentDealData, newDealData } = useDealComparisonData(selectedCreditApp);

    /**
     * Reset form with shopper's last name when slideout opens (only once per session)
     */
    useEffect(() => {
        if (isSlideOutOpen && lastName && !hasInitializedLastName.current) {
            handleFieldChange('lastName', shopperInfo.lastName);
            hasInitializedLastName.current = true;
        }

        // Reset flag when slideout closes
        if (!isSlideOutOpen) {
            hasInitializedLastName.current = false;
        }
    }, [isSlideOutOpen, lastName, handleFieldChange, shopperInfo.lastName]);

    /**
     * Trigger initial search when slideout opens (only once per session)
     */
    useEffect(() => {
        if (isSlideOutOpen && lastName && !hasPerformedInitialSearch.current) {
            performSearch({ lastName, phone: '', email: '' });
            hasPerformedInitialSearch.current = true;
        }

        // Reset flag when slideout closes
        if (!isSlideOutOpen) {
            hasPerformedInitialSearch.current = false;
        }
    }, [isSlideOutOpen, lastName, performSearch]);

    /**
     * Handle slideout close - cancel ongoing search, clear form, results, and dispatch close action
     */
    const handleClose = useCallback(() => {
        cancelSearch();
        clearForm();
        clearResults();
        setSelectedCreditApp(null);
        setCurrentView(CreditAppSearchView.SEARCH);
        dispatch(creditAppSearchActionCreators.closeCreditAppSearchSlideout());
    }, [cancelSearch, clearForm, clearResults, dispatch]);

    /**
     * Handle search button click - calls BFF endpoint
     */
    const handleSearch = useCallback(async () => {
        await performSearch(formData);
    }, [formData, performSearch]);

    /**
     * Handle clear button click - resets form and results
     */
    const handleClear = useCallback(() => {
        clearForm();
        clearResults();
    }, [clearForm, clearResults]);

    /**
     * Handle attach button click - transition to review view
     */
    const handleAttach = useCallback(
        (creditAppId: string) => {
            const selected = searchResults.find((result) => result.creditAppId === creditAppId);
            if (selected) {
                setSelectedCreditApp(selected);
                setCurrentView(CreditAppSearchView.REVIEW);
            }
        },
        [searchResults]
    );

    /**
     * Handle back from review - return to search results
     */
    const handleBackToSearch = useCallback(() => {
        setSelectedCreditApp(null);
        setCurrentView(CreditAppSearchView.SEARCH);
    }, []);

    /**
     * Handle accept changes - merge data and close slideout
     */
    const handleAcceptChanges = useCallback(() => {
        if (selectedCreditApp) {
            const currentDealRefId = orignalDealRefId || originalDealRefIdFs;
            dispatch(
                creditAppSearchActionCreators.mergeCreditAppDeal(
                    selectedCreditApp.rawDeal,
                    currentDealRefId,
                    enableCorrectPhoneHandling
                )
            );
            dispatch(creditAppSearchActionCreators.showCreditAppSearchSaveReminder());
        }
        dispatch(creditAppSearchActionCreators.closeCreditAppSearchSlideout());
    }, [selectedCreditApp, dispatch, enableCorrectPhoneHandling]);

    // Footer configuration based on current view
    const footerProps: FooterActionsType =
        currentView === CreditAppSearchView.SEARCH
            ? {
                  layout: 'rightAligned',
                  primary: {
                      label: 'Cancel',
                      action: handleClose,
                      'data-testid': 'credit-app-search-slideout-review-view-cancel-button',
                      customIdentifiers: {
                          primaryButton: {
                              dataTestId: 'credit-app-search-slideout-review-view-cancel-button'
                          }
                      }
                  }
              }
            : {
                  layout: 'rightAligned',
                  primary: {
                      label: 'Accept Changes',
                      action: handleAcceptChanges,
                      disabled: isLocked,
                      customIdentifiers: {
                          primaryButton: {
                              dataTestId: 'credit-app-search-slideout-review-view-accept-changes-button'
                          }
                      }
                  },
                  options: [
                      {
                          label: 'Cancel',
                          buttonStyle: 'tertiary' as const,
                          action: handleClose,
                          customIdentifiers: {
                              optionButton1: {
                                  dataTestId: 'credit-app-search-slideout-review-view-cancel-button'
                              }
                          }
                      },
                      {
                          label: 'Back',
                          buttonStyle: 'secondary' as const,
                          action: handleBackToSearch,
                          customIdentifiers: {
                              optionButton1: {
                                  dataTestId: 'credit-app-search-slideout-review-view-back-button'
                              }
                          }
                      }
                  ]
              };

    return (
        <SlideOut
            header={<Typography variant="h3">Find & Merge Information</Typography>}
            show={isSlideOutOpen}
            panelWidth="auto"
            position="right"
            onHide={handleClose}
            data-testid="credit-app-search-slideout"
            footer={footerProps}
        >
            <ThemeProvider theme={theme}>
                <SlideoutContainer>
                    {currentView === CreditAppSearchView.SEARCH && (
                        <>
                            <SearchView
                                formData={formData}
                                errors={errors}
                                touched={touched}
                                isSearchButtonEnabled={isSearchButtonEnabled}
                                onFieldChange={handleFieldChange}
                                onFieldBlur={handleFieldBlur}
                                onClear={handleClear}
                                onSearch={handleSearch}
                            />

                            {isLoading && (
                                <LoadingContainer data-testid="search-loading">
                                    <Spinner />
                                    <Typography variant="body-md">Currently searching for Credit Apps...</Typography>
                                </LoadingContainer>
                            )}

                            {!isLoading && (
                                <SearchResultsView
                                    results={searchResults}
                                    isLoading={false}
                                    error={error}
                                    onAttach={handleAttach}
                                    isLocked={isLocked}
                                />
                            )}
                        </>
                    )}

                    {currentView === CreditAppSearchView.REVIEW && selectedCreditApp && newDealData && (
                        <ReviewView currentDealData={currentDealData} newDealData={newDealData} />
                    )}
                </SlideoutContainer>
            </ThemeProvider>
        </SlideOut>
    );
};
